import { morph } from '@theme/morph'
import { Component } from '@theme/component'
import { CartUpdateEvent, ThemeEvents } from '@theme/events'
import { DialogComponent, DialogCloseEvent } from '@theme/dialog'
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities'

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null
  /** @type {Document | null} */
  #cachedProductHtml = null

  #productPageUrl = ''

  get cachedProductHtml() {
    return this.#cachedProductHtml
  }

  connectedCallback() {
    super.connectedCallback()

    this.#productPageUrl = /** @type {HTMLAnchorElement} */ (
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')
    )?.href
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal)
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault()

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage()
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      )

      await this.updateQuickAddModal(freshHtmlCopy)
    }

    this.#openQuickAddModal()
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true)

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      {
        once: true,
      }
    )
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog')
    if (!(dialogComponent instanceof QuickAddDialog)) return

    this.#stayVisibleUntilDialogCloses(dialogComponent)

    dialogComponent.showDialog()
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog')
    if (!(dialogComponent instanceof QuickAddDialog)) return

    dialogComponent.closeDialog()
  }

  /**
   * Fetches the product page content
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage() {
    if (!this.#productPageUrl) return

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort()
    this.#abortController = new AbortController()

    try {
      const response = await fetch(this.#productPageUrl, {
        signal: this.#abortController.signal,
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        )
      }

      const responseText = await response.text()
      const html = new DOMParser().parseFromString(responseText, 'text/html')

      // Store the HTML for later use
      this.#cachedProductHtml = html
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      } else {
        throw error
      }
    } finally {
      this.#abortController = null
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]')
    const modalContent = document.getElementById('quick-add-modal-content')

    if (!productGrid || !modalContent) return

    const variantPicker = productGrid.querySelector('variant-picker')
    if (variantPicker) {
      variantPicker.setAttribute(
        'data-product-form-selector',
        '.quick-add-modal product-form-component'
      )
      variantPicker.setAttribute(
        'data-bis-button-wrapper-selector',
        '.quick-add-modal .variant-picker .product-notification,.quick-add-modal .product-inventory .product-notification'
      )
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details')
      if (!productDetails) return
      const productFormComponent = productGrid.querySelector(
        'product-form-component'
      )
      const productPrice = productGrid.querySelector('product-price')
      const productTitle = document.createElement('a')
      productTitle.textContent = this.dataset.productTitle || ''

      // Make product title as a link to the product page
      productTitle.href = this.#productPageUrl

      if (
        !productFormComponent ||
        !variantPicker ||
        !productPrice ||
        !productTitle
      )
        return

      const productHeader = document.createElement('div')
      productHeader.classList.add('product-header')

      productHeader.appendChild(productTitle)
      productHeader.appendChild(productPrice)
      productGrid.appendChild(productHeader)
      productGrid.appendChild(variantPicker)
      productGrid.appendChild(productFormComponent)
      productDetails.remove()
    }

    const kiwiSizing = productGrid.querySelector('kiwi-sizing-chart')
    if (kiwiSizing) {
      kiwiSizing.remove()
    }

    morph(modalContent, productGrid)
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent)
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController()

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    })
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.#abortController.abort()
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return
    this.closeDialog()
  }

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    )
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.view-product-title a')
    )
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.product-header a')
    )

    if (!anchorElement) return

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href
  }
}

// TODO: BIS (Back In Stock) integration for quick-add modal
// Currently disabled due to app limitation - BIS.popup.show() only works on PDPs, not collection pages
// Future enhancement: Create custom modal and use BIS.create() API directly
// For now, "Notify me" buttons are hidden in quick-add modals via CSS

document.addEventListener('DOMContentLoaded', function () {
  const quickAddModal = document.querySelector('.quick-add-modal')
  if (quickAddModal) {
    // Hide "Notify me" buttons in quick-add modals since BIS popup doesn't work on collection pages
    const bisButtons = quickAddModal.querySelectorAll(
      '.product-notification .bis'
    )
    bisButtons.forEach((button) => {
      /** @type {HTMLElement} */ ;(button).style.display = 'none'
    })
  }
})

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog)
}
